import {
  Alert,
  AnimatedContentLoader,
  Button,
  CopyValue,
  HeaderBlock,
  Input,
  Tab,
  Tabs
} from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { deleteFolder, fetchFolder, IFolder, updateFolder } from '@query'
import { setAPIErrors } from '@utils'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaEnvelopeOpenText, FaInfoCircle, FaTrash } from 'react-icons/fa'
import { FaArrowTurnUp } from 'react-icons/fa6'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  folderId: number
  onClose: () => void
}

export const FolderDetailView: React.FC<Props> = ({ folderId, onClose }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const hasEditPermissions = usePermission(PermissionSet.FILES_EDIT)

  const {
    mutateAsync: deleteMutation,
    isPending: isDeleting,
    isSuccess: isDeleteSuccess
  } = useMutation({
    mutationFn: () => deleteFolder({ slug, folderId }),
    ...{
      onSuccess: async () => {
        onClose()
        await queryClient.invalidateQueries({ queryKey: [slug, 'folders'] })
      }
    }
  })

  const {
    data,
    isLoading: isFolderLoading,
    refetch
  } = useQuery({
    queryKey: [slug, 'folders', folderId],
    queryFn: () => fetchFolder({ slug, folderId }),
    enabled: !(isDeleteSuccess || isDeleting)
  })

  const form = useForm<Partial<IFolder>>({
    defaultValues: async () => {
      const { data } = await refetch()
      return data ?? {}
    }
  })
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, isLoading: isFormLoading, errors }
  } = form

  const { mutate, isPending: isUpdating } = useMutation<IFolder, unknown, Partial<IFolder>>({
    mutationFn: data => updateFolder({ slug, folderId }, data),
    ...{
      onSuccess: updated => {
        reset({
          name: updated.name,
          email_passthrough: updated.email_passthrough
        })
        queryClient.invalidateQueries({ queryKey: [slug, 'folders'] })
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        status === 400 && setAPIErrors(data, setError)
      }
    }
  })

  const onSubmit = handleSubmit(data => mutate(data))

  const isLoading = isFolderLoading || isFormLoading

  return (
    <StyledWrapper>
      <HeaderBlock header={t('files.folderDetails.header')} subHeader={<></>} />

      <ScrollableContent>
        <AnimatedContentLoader isLoading={isLoading}>
          <Tabs>
            <Tab
              key="folder"
              name={t('files.folderDetails.generalSectionHeader')}
              icon={<FaInfoCircle />}
            >
              <StyledForm onSubmit={onSubmit}>
                {data?.for_mobile_uploads ? (
                  <Alert
                    type="info"
                    description={t('files.fileDetails.mobileUploadsDescription')}
                  />
                ) : (
                  <Input
                    {...register('name', {
                      required: { value: true, message: t('validation.required') }
                    })}
                    label={t('files.folderDetails.nameInputLabel')}
                    autoFocus
                    disabled={!hasEditPermissions}
                  />
                )}

                <EmailCard>
                  <EmailImportRow>
                    <IconTile>
                      <FaEnvelopeOpenText />
                    </IconTile>
                    <EmailSectionContent>
                      <h4>{t('files.emailImport.importHeader')}</h4>
                      <p>{t('files.emailImport.importMessage')}</p>
                      {data?.email && <CopyValue value={data.email} />}
                    </EmailSectionContent>
                  </EmailImportRow>

                  <EmailPassthroughRow>
                    <PassthroughGutter>
                      <FaArrowTurnUp />
                    </PassthroughGutter>
                    <EmailSectionContent>
                      <Eyebrow>{t('files.emailPassthrough.eyebrow')}</Eyebrow>
                      <h4>{t('files.emailPassthrough.sectionHeader')}</h4>
                      <p>{t('files.emailPassthrough.description')}</p>
                      <Input
                        {...register('email_passthrough', {
                          setValueAs: value => (value === '' ? null : value),
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: t('validation.invalidEmail')
                          }
                        })}
                        type="email"
                        placeholder={t('files.emailPassthrough.inputPlaceholder')}
                        errors={errors.email_passthrough}
                        disabled={!hasEditPermissions}
                      />
                    </EmailSectionContent>
                  </EmailPassthroughRow>
                </EmailCard>

                {hasEditPermissions && (
                  <StyledActions>
                    <Button
                      disabled={!isDirty || isLoading}
                      intent="success"
                      showSpinner={isUpdating}
                    >
                      {t('general.save')}
                    </Button>
                  </StyledActions>
                )}
              </StyledForm>
            </Tab>

            {hasEditPermissions && (
              <Tab
                key="delete"
                name={t('files.folderDetails.deleteSectionHeader')}
                icon={<FaTrash />}
              >
                <Alert
                  type="warning"
                  isVisible={data?.has_content}
                  title={t('files.folderDetails.deleteDeniedTitle')}
                  description={t('files.folderDetails.deleteDeniedDescription')}
                />

                <Alert
                  type="info"
                  isVisible={!data?.has_content}
                  description={t('files.folderDetails.deleteOkDescription')}
                />

                <StyledActions>
                  <Button
                    intent="danger"
                    icon={<FaTrash />}
                    showSpinner={isDeleting}
                    disabled={data?.has_content || isDeleting}
                    onClick={() => deleteMutation()}
                  >
                    {t('general.delete')}
                  </Button>
                </StyledActions>
              </Tab>
            )}
          </Tabs>
        </AnimatedContentLoader>
      </ScrollableContent>
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  display: flex;
  flex-direction: column;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.xl}rem;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  max-width: 680px;
`

const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const EmailCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.defaultHover};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
`

const EmailImportRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

const EmailPassthroughRow = styled(EmailImportRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.borderSoft};
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const IconTile = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => `color-mix(in srgb, ${theme.colors.primary} 12%, white)`};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 19px;
    height: 19px;
  }
`

const EmailSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  flex: 1;

  h4 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }
`

const PassthroughGutter = styled.div`
  width: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: ${({ theme }) => theme.colors.iconGray};
  padding-top: 2px;

  svg {
    width: 16px;
    height: 16px;
    transform: rotate(90deg);
    transform-origin: center;
  }
`

const Eyebrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.iconGray};
`
